import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Typography, Card, CardActionArea, CardContent, Box } from '@mui/material';
import { DynamicIcon } from '../../components/icons';
import { PageHeader, StandardPageLayout } from '../../components/Common';

import { useAuth } from '../../context/useAuth';
import { isPlatformScoped } from '../../utils/permissions';
import ConnectDataPanel from '../../components/TenantPortal/ConnectDataPanel';

const MyAppsPage = ({ appBundles, bundleId: propBundleId }) => {
    const { user: currentUser } = useAuth();
    // Allow platform admins, Tenant Admins (r2), and r4 to access Data Settings on the card
    const isAdmin = isPlatformScoped(currentUser) || ['r2', 'r4'].includes(currentUser?.roleId);
    const [settingsBundleId, setSettingsBundleId] = React.useState(null);

    const params = useParams();
    const bundleId = propBundleId || params.bundleId;

    const activeBundle = bundleId
        ? appBundles.find(b => b.id === bundleId)
        : null;

    const itemsToDisplay = activeBundle ? activeBundle.contents : appBundles;
    const pageTitle = activeBundle ? activeBundle.name : "My Apps";

    const appPath = (tenantId, targetBundleId, contentUid = null) => {
        const base = `/apps/${targetBundleId}`;
        return contentUid ? `${base}/${contentUid}` : base;
    };

    return (
        <StandardPageLayout
            title={pageTitle}
            description={
                activeBundle
                    ? `Showing all content within the "${activeBundle.name}" bundle.`
                    : "Access your personalized suite of analytics and business tools designed to drive insights and efficiency."
            }
            headerActions={
                activeBundle && (
                    <Link to="/my_apps" className="flex items-center text-primary hover:underline">
                        <DynamicIcon name="ArrowLeft" className="mr-2 h-4 w-4" />
                        Back to All Apps
                    </Link>
                )
            }
        >
            {itemsToDisplay && itemsToDisplay.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {itemsToDisplay.map(item => {
                        let linkPath;
                        if (item.isBundle) {
                            // Direct navigation to bundle contents list
                            linkPath = appPath(currentUser?.tenantId, item.id);
                        } else if (bundleId === 'individual-apps') {
                            // Fix: Items in 'individual-apps' are instances, so link to them directly
                            linkPath = appPath(currentUser?.tenantId, item.id);
                        } else {
                            // Standard bundle content
                            const targetContentUid = bundleId && bundleId !== item.id ? item.id : null;
                            linkPath = appPath(currentUser?.tenantId, bundleId || item.id, targetContentUid);
                        }

                        return (
                            <Card
                                key={item.id}
                                className="shadow-lg transition-transform transform hover:-translate-y-1 overflow-hidden h-full dark:bg-gray-800 dark:border-gray-700"
                                sx={{ height: '290px', display: 'flex', flexDirection: 'column', position: 'relative' }}
                            >
                                {isAdmin && item.isBundle && (
                                    <button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setSettingsBundleId(item.id);
                                        }}
                                        className="absolute top-3 right-3 z-10 p-2 rounded-full bg-white/80 hover:bg-gray-100 dark:bg-gray-800/80 dark:hover:bg-gray-700 shadow-sm border border-gray-200 dark:border-gray-600 text-gray-500 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400 transition-colors focus:outline-none"
                                        title="Data Settings"
                                    >
                                        <DynamicIcon name="Database" size={16} />
                                    </button>
                                )}
                                <CardActionArea component={Link} to={linkPath} sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start' }}>
                                    <div className="flex flex-col items-center text-center p-4 h-full overflow-hidden relative w-full">
                                        <DynamicIcon
                                            name={item.icon || 'Package'}
                                            size={40}
                                            className="mb-3 text-primary"
                                        />
                                        <Typography variant="h6" className="mb-2 w-full break-words text-center dark:text-white" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                                            {item.name}
                                        </Typography>

                                        {/* ✅ reverted clean truncation — now limited to 6 lines */}
                                        <Typography
                                            variant="body2"
                                            className="text-sm text-gray-500 dark:text-gray-300 overflow-hidden mb-2"
                                            sx={{
                                                display: '-webkit-box',
                                                WebkitLineClamp: 6, // ✅ reduced from 7 to 6
                                                WebkitBoxOrient: 'vertical',
                                                lineHeight: '1.4em',
                                                maxHeight: '8.4em', // 6 × 1.4em
                                                textOverflow: 'ellipsis',
                                                overflowWrap: 'break-word',
                                                wordBreak: 'break-word'
                                            }}
                                        >
                                            {item.description ||
                                                (item.isBundle
                                                    ? `${item.contents?.length || 0} items`
                                                    : 'Application Content')}
                                        </Typography>
                                    </div>
                                </CardActionArea>
                            </Card>
                        );
                    })}
                </div>
            ) : (
                <div className="flex flex-col justify-center items-center h-64 text-gray-500">
                    <DynamicIcon name="PackageOpen" className="w-12 h-12 mb-4 opacity-50" />
                    <Typography>No applications have been assigned to your tenant yet.</Typography>
                </div>
            )}
            
            {settingsBundleId && (
                <ConnectDataPanel
                    bundleId={settingsBundleId}
                    appName={appBundles.find(b => b.id === settingsBundleId)?.name || 'App'}
                    onClose={() => setSettingsBundleId(null)}
                />
            )}
        </StandardPageLayout>
    );
};

export default MyAppsPage;
